import { createCriteriaParameters, standardDeleteQuery, standardGetQuery, standardPostQuery, standardPutQuery } from "./apiUtils.js";
import { replacePlaceholdersWithObjValues, setDeleteArrayState, setInsertArrayState } from "../shared/miscUtils.js";

import { SNACKBAR_SUCCESS_CREATED, SNACKBAR_SUCCESS_DELETED, SNACKBAR_SUCCESS_UPDATED } from "../../config/language/english.js";
import * as Constants from "../../config/constants/Constants.js";

/**
 * Query lightweight examination history for a patient (no teeth_data).
 */
export const queryPatientExaminations = (props, patientId, options = {}, callback = null) => {
    const path =
        replacePlaceholdersWithObjValues(Constants.API_PATIENTS_ID_EXAMINATIONS, { patient_id: patientId }) + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        if (callback) callback(response.body);
    });
};

/**
 * Fetch all examination summaries for a patient by paging through results.
 */
export const queryAllPatientExaminations = (props, patientId, callback = null) => {
    const pageSize = 100;
    const collected = [];

    const fetchPage = (page) => {
        queryPatientExaminations(
            props,
            patientId,
            {
                page,
                pageSize,
                pagination: false
            },
            (body) => {
                collected.push(...(body?.elements || []));

                const total = body?.total ?? collected.length;
                if (collected.length < total) {
                    fetchPage(page + 1);
                    return;
                }

                if (callback) callback(collected);
            }
        );
    };

    fetchPage(0);
};

/**
 * Function for querying examinations with filters, pagination, sorting etc.
 */
export const queryExaminations = (props, options = {}) => {
    const path = Constants.API_EXAMINATIONS + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.examinations;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.pagination === false) return;

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying an existing examination.
 */
export const queryExamination = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_EXAMINATIONS_ID, { examination_id: options.id });

    standardGetQuery(props, path, (response) => {
        const states = props.backend.examinations;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        if (callback) callback(response.body);
    });
};

/**
 * Fetch a single examination without requiring global cache updates.
 */
export const fetchExaminationById = (props, id, callback = null, errorCallback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_EXAMINATIONS_ID, { examination_id: id });

    standardGetQuery(
        props,
        path,
        (response) => {
            if (callback) callback(response.body);
        },
        errorCallback ? { onError: errorCallback } : {}
    );
};

/**
 * Function for creating a new examination.
 */
export const createExamination = (props, options = {}, callback = null) => {
    const path = Constants.API_EXAMINATIONS;

    standardPostQuery(props, path, options.body, (response) => {
        const states = props.backend.examinations;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setRowCount((prev) => prev + 1);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_CREATED });
        if (callback) callback(response);
    });
};

/**
 * Function for updating a examination.
 */
/**
 * Move an examination onto a different patient record.
 */
export const reassignExamination = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_EXAMINATIONS_ID_PATIENT, { examination_id: options.id });

    standardPutQuery(props, path, { patient_id: options.patientId }, (response) => {
        const states = props.backend.examinations;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setSelected([]);

        props.showSnackbar({ message: "Examination moved to the selected patient." });
        if (callback) callback(response);
    });
};

export const updateExamination = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_EXAMINATIONS_ID, { examination_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const states = props.backend.examinations;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
        if (callback) callback(response);
    });
};

/**
 * Function for approving a examination.
 */
export const approveExamination = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_EXAMINATIONS_ID_APPROVE, { examination_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.examinations;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
        if (callback) callback(response);
    });
};

/**
 * Function for approving a examination.
 */
export const approveExaminations = (props, options = {}) => {
    standardPutQuery(props, Constants.API_EXAMINATIONS_APPROVE, options.body, (response) => {
        const states = props.backend.examinations;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for querying the latest examination for a patient.
 * Used to sync missing teeth and implants when creating a new examination.
 */
export const queryLatestPatientExamination = (props, patientId, callback = null) => {
    const path =
        Constants.API_EXAMINATIONS +
        createCriteriaParameters({
            filter: { items: [{ columnField: "patient_id", operatorValue: "equals", value: patientId }] },
            sort: [{ field: "created_on", sort: "desc" }],
            pageSize: 1,
            page: 0
        });

    standardGetQuery(props, path, (response) => {
        const latestExamination = response?.body?.elements?.[0] || null;
        if (callback) callback(latestExamination);
    });
};

/**
 * Function for deleting examination(s).
 */
export const deleteExaminations = (props, options = {}) => {
    const path = Constants.API_EXAMINATIONS;

    standardDeleteQuery(props, path, options.body, (response) => {
        const states = props.backend.examinations;

        setDeleteArrayState(states.setData, options.ids);

        states.setRowCount(states.rowCount - options.ids.length);
        states.setSelected([]);

        // Delete all child examinations children to it from states.
        // const children = props.backend.examinations;
        // const childrenIds = children.data.filter((t) => options.ids.includes(t.parent_id)).map((t) => t.id);

        // setDeleteArrayState(children.setData, childrenIds);
        // children.setRowCount(Math.max(0, children.rowCount - childrenIds.length));
        // children.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};
